import { useRef, useState } from "react";
import { $getRoot } from "lexical";
import type { SerializedEditorState } from "lexical";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store";
import ProfileLoader from "../../components/ABOProfile/ProfileLoader/ProfileLoader";
import LeafLinkButton from "../../common/leaf/LeafLinkButton/LeafLinkButton";
import { addNoteClickTrack, editNoteClickTrack } from "../../common/tealium/CustomerProfile/Note/NoteTrack";
import {
  aboProfileAPI,
  useCreateProfileNoteMutation,
  useDeleteProfileNoteMutation,
  useGetProfileNotesQuery,
  useUpdateProfileNoteMutation,
} from "../../services/AboProfile/AboProfileAPI";
import defaultContent from "../../content/profile";
import ViewNote from "./ViewNote/ViewNote";
import EditNote from "./EditNote/EditNote";
import { NotesEditor } from "../DownlineProfile/Tabs/Notes/NotesEditor/NotesEditor";
import { parseLexicalNote } from "../DownlineProfile/Tabs/Notes/notesConfig";
import { logger } from "../../common/util/logger";
import "../DownlineProfile/Tabs/Notes/Notes.scss";
import "./Note.scss";

const Note = () => {
  const { t } = useTranslation(["profile"]);

  const editButtonText = `${t(
    "customerProfile.note.editButton",
    defaultContent["customerProfile"]["note"]["editButton"],
  )}`;
  const saveButtonText = `${t(
    "customerProfile.note.saveButton",
    defaultContent["customerProfile"]["note"]["saveButton"],
  )}`;
  const addButtonText = `${t(
    "customerProfile.note.addButton",
    defaultContent["customerProfile"]["note"]["addButton"],
  )}`;

  const dispatch = useAppDispatch();

  const affAbo = useAppSelector((state) => state.profile.affAbo);
  const { showNote = false, disableImpersonationNoteSave = false } =
    useAppSelector(({ boot }) => boot?.configuration) || {};
  const isImpersonation = useAppSelector(({ boot }) => boot.isImpersonation);
  const noteSaveDisabled = isImpersonation === true && disableImpersonationNoteSave === true;

  const { isFetching, data } = useGetProfileNotesQuery({ affabo: affAbo }, { skip: !affAbo });
  const { noteId = "", content = "" } = data?.notes?.[0] || {};
  const richContent = parseLexicalNote(content);
  const originalRichContent = richContent ? (JSON.parse(content) as SerializedEditorState) : null;
  const editorContent = originalRichContent?.root.children.length
    ? originalRichContent
    : (richContent?.toJSON() ?? null);
  const plainContent = richContent ? richContent.read(() => $getRoot().getTextContent()) : content;

  const [updateNote] = useUpdateProfileNoteMutation();
  const [createNote] = useCreateProfileNoteMutation();
  const [deleteNote] = useDeleteProfileNoteMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatedNote, setUpdatedNote] = useState("");
  const richEditorChanged = useRef(false);
  const originalEditorContent = useRef<SerializedEditorState | null>(null);

  const _isFetching = showNote === true && isFetching === true && !isEditing;

  // Dispatch an optimistic update to the profile-notes query cache.
  const updateProfileNotesQuery = (updatedNoteId: string, updatedContent: string) => {
    dispatch(
      aboProfileAPI.util.updateQueryData("getProfileNotes", { affabo: affAbo }, (draft) => {
        if (!updatedNoteId) {
          draft.notes = [];
          return;
        }

        const currentNote = draft.notes[0];
        draft.notes = [
          {
            noteId: updatedNoteId,
            affAbo: currentNote?.affAbo ?? affAbo,
            content: updatedContent,
            contentFormat: currentNote?.contentFormat,
            createdAt: currentNote?.createdAt ?? Date.now(),
            lastModified: currentNote?.lastModified ?? Date.now(),
          },
        ];
      }),
    );
  };

  const createNewNote = async () => {
    if (noteSaveDisabled) return;
    if (!noteId && updatedNote) {
      updateProfileNotesQuery("temp", updatedNote);

      const result = await createNote({
        affabo: affAbo,
        content: updatedNote,
      }).unwrap();

      if (result) {
        updateProfileNotesQuery(result.noteId, updatedNote);
      }
    }
  };

  const updateNoteContent = async () => {
    if (noteSaveDisabled) return;
    if (noteId && updatedNote) {
      updateProfileNotesQuery(noteId, updatedNote);
      await updateNote({
        noteId,
        content: updatedNote,
      }).unwrap();
    }
  };

  const deleteNoteContent = async () => {
    if (noteSaveDisabled) return;
    if (noteId && updatedNote.length === 0) {
      updateProfileNotesQuery("", updatedNote);
      await deleteNote({ noteId }).unwrap();
    }
  };

  const addNoteButtonOnClick = () => {
    if (noteSaveDisabled) return;
    setUpdatedNote("");
    setIsEditing(true);
    addNoteClickTrack();
  };

  const editButtonOnClick = () => {
    if (noteSaveDisabled) return;
    setIsEditing(true);
    setUpdatedNote(plainContent);
    richEditorChanged.current = false;
    originalEditorContent.current = editorContent;
    editNoteClickTrack();
  };

  const saveNoteButtonOnClick = async () => {
    if (noteSaveDisabled) return;

    try {
      setIsUpdating(true);
      setIsEditing(false);
      if (!noteId || updatedNote !== plainContent) {
        await createNewNote();
        await updateNoteContent();
        await deleteNoteContent();
      }
    } catch (error) {
      logger.error("Failed to save note", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const saveRichNote = async (state: SerializedEditorState) => {
    if (noteSaveDisabled || !originalEditorContent.current) return;
    const parsed = parseLexicalNote(JSON.stringify(state));
    try {
      setIsUpdating(true);
      if (!richEditorChanged.current || JSON.stringify(state) === JSON.stringify(originalEditorContent.current)) {
        setIsEditing(false);
        return;
      }
      if (parsed && parsed.read(() => $getRoot().getTextContent()).length === 0) {
        await deleteNote({ noteId }).unwrap();
        updateProfileNotesQuery("", "");
      } else {
        const serialized = JSON.stringify(state);
        await updateNote({ noteId, content: serialized }).unwrap();
        updateProfileNotesQuery(noteId, serialized);
      }
      setIsEditing(false);
    } catch (error) {
      logger.error("Failed to save rich note", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const noteOnChange = (note: string) => {
    setUpdatedNote(note);
  };

  if (_isFetching === false) {
    return (
      <div className="note note__container">
        <div className="note__container__content">
          <div className={`note__title${noteId || isEditing ? "" : "--hidden"}`}>
            <h2 className="note__title__text">{`${t(
              "customerProfile.note.pageTitle",
              defaultContent["customerProfile"]["note"]["pageTitle"],
            )}`}</h2>
            <LeafLinkButton
              as="link"
              onClick={editButtonOnClick}
              label={editButtonText}
              theme="blue"
              fontWeight="bold"
              fontSize="medium"
              show={isEditing === false}
              disabled={isUpdating === true || noteSaveDisabled}
            >
              {editButtonText}
            </LeafLinkButton>
            <LeafLinkButton
              as="link"
              onClick={saveNoteButtonOnClick}
              label={saveButtonText}
              theme="blue"
              fontWeight="bold"
              fontSize="medium"
              show={isEditing === true && !richContent}
              disabled={isUpdating === true || noteSaveDisabled}
            >
              {saveButtonText}
            </LeafLinkButton>
          </div>
          <div className={`note__container__content__viewNote${isEditing ? "--hidden" : ""}`}>
            <ViewNote noteId={noteId} content={plainContent} />
          </div>
          {isEditing && richContent ? (
            <NotesEditor
              initialContent={editorContent}
              onSave={saveRichNote}
              onCancel={() => setIsEditing(false)}
              onContentChange={() => {
                richEditorChanged.current = true;
              }}
              isSaving={isUpdating}
              allowEmptySave
              saveDisabled={noteSaveDisabled}
            />
          ) : (
            <EditNote value={updatedNote} noteOnChange={noteOnChange} show={isEditing} />
          )}
        </div>
        <div className="note__container__content-btn">
          <LeafLinkButton
            onClick={addNoteButtonOnClick}
            label={addButtonText}
            as="button"
            fontWeight="bold"
            fontSize="medium"
            show={noteId === "" && isEditing === false}
            disabled={isUpdating === true || noteSaveDisabled}
          >
            {addButtonText}
          </LeafLinkButton>
        </div>
      </div>
    );
  }

  return (
    <div className="note__container__loader">
      <ProfileLoader row={1} card={true} isFetching={isFetching} />
    </div>
  );
};

export default Note;
